import { HTMLAttributes, PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { ContentLoader, Heading, NakedButton, Text, Truncated, styles as dsStyles } from '@frontend/design-system';
import { TruncatedText } from '../../truncated-text';
import { BackgroundConfig, RoiCardBaseProps, SummaryItemProps } from './types';

interface RoiOverviewCardProps extends RoiCardBaseProps {
  iconName: IconName;
  onScreenCardsCount?: number;
}

const defaultBackgroundConfig: BackgroundConfig = {
  gradientDegree: 135,
  gradientEnd: '#94BFFF', // Color not available in the theme
  gradientStart: theme.colors.primary40,
};

const chartWidth = 144;

const SummaryItem = ({ label, value, ...rest }: SummaryItemProps & HTMLAttributes<HTMLDivElement>) => (
  <div {...rest}>
    <Text css={styles.value} textAlign='left' weight='bold'>
      {value}
    </Text>
    <Truncated css={styles.label}>{label}</Truncated>
  </div>
);

const getDataItemCss = (count?: number) => {
  switch (count) {
    case 1:
      return styles.oneCard;

    case 2:
      return styles.twoCards;

    default:
      return [styles.twoCards, styles.moreThanTwoCards];
  }
};

export const RoiOverviewCard = ({
  backgroundConfig,
  children,
  filteredDate,
  iconName,
  isLoading,
  onClick,
  onScreenCardsCount,
  summaryValues = [],
  title,
  trackingId,
}: PropsWithChildren<RoiOverviewCardProps>) => {
  const background = { ...defaultBackgroundConfig, ...backgroundConfig };

  return (
    <NakedButton
      className='roi-overview-card-button'
      css={styles.wrapper}
      onClick={onClick}
      style={{
        background: `linear-gradient(${background?.gradientDegree}deg, ${background?.gradientStart}, ${background?.gradientEnd})`,
      }}
      trackingId={trackingId}
    >
      <header>
        <div css={styles.flexGroup} style={{ maxWidth: 'calc(100% - 116px)' }}>
          <Icon name={iconName} size={16} />
          <Heading css={dsStyles.truncate} level={3} style={{ fontSize: theme.fontSize(14) }}>
            <TruncatedText as='span' css={styles.truncatedText} text={title} />
          </Heading>
        </div>
        <div css={styles.flexGroup}>
          <Icon name='calendar' size={16} />
          <Text style={{ fontSize: theme.fontSize(14), whiteSpace: 'nowrap' }}>{filteredDate}</Text>
        </div>
      </header>

      <div className='roi-overview-card-data-items' css={[styles.dataItems, getDataItemCss(onScreenCardsCount)]}>
        {summaryValues.map((summaryItem) => (
          <SummaryItem {...summaryItem} key={summaryItem.label} className={`summary-item ${summaryItem.className}`} />
        ))}

        {children}
      </div>
      <ContentLoader show={isLoading} size='small' />
    </NakedButton>
  );
};

const styles = {
  wrapper: css`
    border-radius: ${theme.borderRadius.medium};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: ${theme.spacing(2)};
    position: relative;
    width: 100%;

    header {
      display: flex;
      gap: ${theme.spacing(2)};
      justify-content: space-between;
      width: 100%;
    }

    * {
      color: ${theme.colors.white} !important;
    }
  `,

  flexGroup: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(0.5)};

    svg {
      min-width: ${theme.spacing(2)};
    }
  `,

  dataItems: css`
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: ${theme.spacing(2)};
    justify-content: space-between;
    margin-top: ${theme.spacing(1)};
    width: 100%;

    .summary-item {
      max-width: calc(100% - ${chartWidth}px - ${theme.spacing(2)});
    }
  `,

  truncatedText: css`
    display: block;
    width: 100%;
  `,

  oneCard: css`
    .messages-sent {
      display: none;

      @container (min-width: 580px) {
        display: block;
      }
    }

    .summary-item {
      @container (min-width: 580px) {
        max-width: calc((100% - ${chartWidth}px) / 2 - ${theme.spacing(2)});
      }
    }
  `,

  twoCards: css`
    .messages-sent {
      display: none;

      @container (min-width: 1080px) {
        display: block;
      }
    }

    .summary-item {
      @container (min-width: 1080px) {
        max-width: calc((100% - ${chartWidth}px) / 2 - ${theme.spacing(2)});
      }
    }
  `,

  moreThanTwoCards: css`
    .messages-sent {
      display: none;
    }

    .summary-item {
      max-width: calc(100% - ${chartWidth}px - ${theme.spacing(2)});
    }
  `,

  label: css`
    font-size: ${theme.font.size.medium};
    min-width: 0;
    text-align: left;
  `,

  value: css`
    font-size: ${theme.fontSize(48)};
    line-height: 1;
    overflow: hidden;
    white-space: nowrap;
  `,
};
